import { CHAPTERS, TUTORIAL_STEPS, isAvailable, placementOf, stepIndex, type Capabilities, type PlacementContext, type TutorialStep } from './registry'

// The tutorial state machine: a pure reducer, no timers, no I/O. TutorialHost feeds it and persists `chapters`.
export type Mode = 'idle' | 'offer' | 'waiting' | 'showing' | 'paused' | 'chapterDone' | 'finished'
export type PauseReason = 'user' | 'anchor-timeout' | 'request-failed'
export type ChapterStatus = 'not_started' | 'in_progress' | 'paused' | 'skipped' | 'completed'

export interface ChapterRecord {
  status: ChapterStatus
  stepId: string | null
  viewed: string[]
  practiced: string[]
}

export interface TutorialState {
  mode: Mode
  pauseReason: PauseReason | null
  index: number
  /** Bus sequence when the current step began; confirmed events at or before it are stale. */
  since: number
  /** The anchor of a content step is missing: the empty-state explanation shows instead. */
  empty: boolean
  workspaceId: string | null
  /** The studio item a chapter works on; later events in that chapter must be for the same item. */
  entityId: string | null
  entityChapter: string | null
  ctx: PlacementContext
  caps: Capabilities | null
  chapters: Record<string, ChapterRecord>
}

export type TutorialEvent =
  | { type: 'HYDRATE'; chapters: Record<string, ChapterRecord>; offer: boolean }
  | { type: 'CONTEXT'; ctx: PlacementContext; caps: Capabilities | null }
  | { type: 'START'; at: number; workspaceId: string | null; stepId?: string }
  | { type: 'LATER' }
  | { type: 'NEXT'; at: number }
  | { type: 'BACK'; at: number }
  | { type: 'CONTINUE_READONLY'; at: number }
  | { type: 'SKIP_STEP'; at: number }
  | { type: 'PAUSE' }
  | { type: 'RESUME'; at: number }
  | { type: 'EXIT' }
  | { type: 'RESTART_CHAPTER'; chapter: string; at: number }
  | { type: 'START_OVER'; at: number }
  | { type: 'ANCHOR_READY'; stepId: string }
  | { type: 'ANCHOR_TIMEOUT'; stepId: string }
  | { type: 'REQUEST_FAILED'; event: string }
  | { type: 'ACTION_CONFIRMED'; event: string; seq: number; workspaceId: string | null; entityId: string | null }
  | { type: 'IDENTITY_CHANGED' }

const WORKSPACE_EVENTS = new Set(['workspace-created', 'workspace-activated'])

export function initialState(): TutorialState {
  return {
    mode: 'idle', pauseReason: null, index: 0, since: 0, empty: false, workspaceId: null,
    entityId: null, entityChapter: null, ctx: { authenticated: false, onboarded: false }, caps: null, chapters: {},
  }
}

export const currentStep = (s: TutorialState): TutorialStep => TUTORIAL_STEPS[s.index]

export function chapterRecord(s: TutorialState, chapter: string): ChapterRecord {
  return s.chapters[chapter] ?? { status: 'not_started', stepId: null, viewed: [], practiced: [] }
}

export const isPracticed = (s: TutorialState, step: TutorialStep): boolean => chapterRecord(s, step.chapter).practiced.includes(step.id)

/** Info for this user now: an info step, a restricted or empty step, or T01 taught from the account status card. */
export function isInfo(s: TutorialState, step: TutorialStep = currentStep(s)): boolean {
  return step.kind === 'info' || !isAvailable(step, s.caps) || s.empty
    || (step.chapter === 'T01' && placementOf(step, s.ctx).branch === 'returning')
}

/** First step of the first chapter left paused or in progress, else of the first chapter not finished. */
export function resumeIndex(s: TutorialState): number {
  const open = CHAPTERS.find((c) => ['paused', 'in_progress'].includes(chapterRecord(s, c.id).status))
  const stored = open && chapterRecord(s, open.id).stepId
  if (stored && stepIndex(stored) >= 0) return stepIndex(stored)
  const next = open ?? CHAPTERS.find((c) => chapterRecord(s, c.id).status === 'not_started')
  return next ? stepIndex(next.stepIds[0]) : 0
}

const addOnce = (list: string[], id: string) => (list.includes(id) ? list : [...list, id])

function patchChapter(s: TutorialState, chapter: string, patch: Partial<ChapterRecord>): TutorialState {
  return { ...s, chapters: { ...s.chapters, [chapter]: { ...chapterRecord(s, chapter), ...patch } } }
}

function enter(s: TutorialState, index: number, at: number): TutorialState {
  const step = TUTORIAL_STEPS[index]
  const rec = chapterRecord(s, step.chapter)
  const next = patchChapter(s, step.chapter, { stepId: step.id, status: rec.status === 'completed' ? 'completed' : 'in_progress' })
  const mode = isAvailable(step, s.caps) ? 'waiting' : 'showing'
  return { ...next, mode, pauseReason: null, index, since: at, empty: false }
}

function record(s: TutorialState, list: 'viewed' | 'practiced'): TutorialState {
  const step = currentStep(s)
  return patchChapter(s, step.chapter, { [list]: addOnce(chapterRecord(s, step.chapter)[list], step.id) })
}

function advance(s: TutorialState, at: number): TutorialState {
  const step = currentStep(s)
  const chapter = CHAPTERS.find((c) => c.id === step.chapter)!
  if (step.id !== chapter.stepIds[chapter.stepIds.length - 1]) return enter(s, s.index + 1, at)
  const rec = chapterRecord(s, step.chapter)
  const covered = chapter.stepIds.every((id) => rec.viewed.includes(id) || rec.practiced.includes(id))
  const status: ChapterStatus = rec.status === 'completed' || covered ? 'completed' : 'skipped'
  return { ...patchChapter(s, step.chapter, { status }), mode: 'chapterDone', pauseReason: null, empty: false }
}

function pauseChapter(s: TutorialState): TutorialState {
  const ch = currentStep(s).chapter
  return chapterRecord(s, ch).status === 'in_progress' ? patchChapter(s, ch, { status: 'paused' }) : s
}

const ACTIVE: Mode[] = ['waiting', 'showing', 'paused']

export function reduce(s: TutorialState, e: TutorialEvent): TutorialState {
  switch (e.type) {
    case 'HYDRATE':
      return { ...s, chapters: e.chapters, mode: e.offer && s.mode === 'idle' ? 'offer' : s.mode }
    case 'CONTEXT':
      return { ...s, ctx: e.ctx, caps: e.caps }
    case 'START':
      return enter({ ...s, workspaceId: e.workspaceId, entityId: null, entityChapter: null }, e.stepId ? stepIndex(e.stepId) : resumeIndex(s), e.at)
    case 'LATER':
      if (s.mode !== 'offer') return s
      return { ...patchChapter(s, 'T01', { status: 'paused', stepId: TUTORIAL_STEPS[0].id }), mode: 'idle' }
    case 'ANCHOR_READY':
      return s.mode === 'waiting' && currentStep(s).id === e.stepId ? { ...s, mode: 'showing' } : s
    case 'ANCHOR_TIMEOUT':
      if (s.mode !== 'waiting' || currentStep(s).id !== e.stepId) return s
      return currentStep(s).needsContent
        ? { ...s, mode: 'showing', empty: true }
        : { ...s, mode: 'paused', pauseReason: 'anchor-timeout' }
    case 'REQUEST_FAILED': {
      const step = currentStep(s)
      if (!['waiting', 'showing'].includes(s.mode) || isInfo(s) || isPracticed(s, step)) return s
      return [step.event, ...step.accepts].includes(e.event) ? { ...s, mode: 'paused', pauseReason: 'request-failed' } : s
    }
    case 'ACTION_CONFIRMED': {
      const step = currentStep(s)
      if (!['waiting', 'showing'].includes(s.mode) || isInfo(s) || e.seq <= s.since) return s
      if (![step.event, ...step.accepts].includes(e.event)) return s
      const workspaceEvent = WORKSPACE_EVENTS.has(e.event)
      if (!workspaceEvent && e.workspaceId && s.workspaceId && e.workspaceId !== s.workspaceId) return s
      const sameChapter = s.entityChapter === step.chapter
      if (e.entityId && s.entityId && sameChapter && e.entityId !== s.entityId) return s
      const next = record(s, 'practiced')
      return {
        ...next,
        mode: 'showing',
        workspaceId: workspaceEvent ? e.workspaceId : s.workspaceId,
        entityId: e.entityId ?? (sameChapter ? s.entityId : null),
        entityChapter: e.entityId ? step.chapter : s.entityChapter,
      }
    }
    case 'NEXT':
      if (s.mode === 'chapterDone') {
        return s.index === TUTORIAL_STEPS.length - 1 ? { ...s, mode: 'finished' } : enter(s, s.index + 1, e.at)
      }
      if (s.mode !== 'showing') return s
      if (isPracticed(s, currentStep(s))) return advance(s, e.at)
      return isInfo(s) ? advance(record(s, 'viewed'), e.at) : s
    case 'CONTINUE_READONLY':
      return ACTIVE.includes(s.mode) ? advance(record(s, 'viewed'), e.at) : s
    case 'SKIP_STEP':
      return ACTIVE.includes(s.mode) ? advance(s, e.at) : s
    case 'BACK':
      if (s.mode === 'chapterDone') return enter(s, s.index, e.at)
      return ACTIVE.includes(s.mode) && s.index > 0 ? enter(s, s.index - 1, e.at) : s
    case 'PAUSE':
      return ['waiting', 'showing'].includes(s.mode) ? { ...pauseChapter(s), mode: 'paused', pauseReason: 'user' } : s
    case 'RESUME':
      if (s.mode === 'paused') return { ...s, mode: 'waiting', pauseReason: null, since: e.at, empty: false }
      return ['idle', 'offer', 'finished'].includes(s.mode) ? enter(s, resumeIndex(s), e.at) : s
    case 'EXIT':
      return s.mode === 'idle' ? s : { ...(ACTIVE.includes(s.mode) ? pauseChapter(s) : s), mode: 'idle', pauseReason: null, empty: false }
    case 'RESTART_CHAPTER': {
      const chapter = CHAPTERS.find((c) => c.id === e.chapter)
      return chapter ? enter(s, stepIndex(chapter.stepIds[0]), e.at) : s
    }
    case 'START_OVER':
      return enter(s, 0, e.at)
    case 'IDENTITY_CHANGED':
      return initialState()
  }
}
